import { useMemo, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { Alert, Button, GenericCombobox, GenericDialog, InputGroup } from '@/common'
import { useTranslation } from 'react-i18next'
import { useSession } from '@/shared/hooks/useSession'
import { useInviteOrganizationUser, useOrganizationUserRoles } from '@/shared/hooks/api/organizationUsers'
import { organizationUserInvitePayloadSchema } from '@/api/OrganizationUserModel'
import EmailInputGroup from '@/shared/components/forms/EmailInputGroup'

type FormState = {
  firstName: string
  lastName: string
  email: string
  roleSlug: string | null
}

const getInitialState = (): FormState => ({
  firstName: '',
  lastName: '',
  email: '',
  roleSlug: null
})

export default function InviteOrganizationUserDialog({
  isOpen,
  close
}: {
  isOpen: boolean
  close: () => void
}) {
  const { t } = useTranslation()

  const [state, setState] = useState<FormState>(getInitialState())

  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()

  const {
    mutateAsync: inviteOrganizationUser,
    isPending: isInviting,
    error
  } = useInviteOrganizationUser(organizationId)

  const { data: roles } = useOrganizationUserRoles(organizationId)

  const onClose = useCallback(() => {
    setState(getInitialState())
    close()
  }, [close])

  const validationResult = useMemo(() => {
    return organizationUserInvitePayloadSchema.safeParse(state)
  }, [state])

  const onAdd = useCallback(async () => {
    if (!validationResult.success) {
      return
    }
    await inviteOrganizationUser(validationResult.data)
    toast.success(t('organizationUsers:inviteUserDialog.success'))
    setState(getInitialState())
    onClose()
  }, [inviteOrganizationUser, validationResult, onClose, t])

  const roleOptions = useMemo(
    () =>
      roles?.map(role => ({
        label: t(`organizationUsers:roles.${role}`),
        value: role
      })),
    [roles]
  )

  const onInputChange = useCallback(
    (field: keyof FormState, value: string | null) => {
      setState(prev => ({ ...prev, [field]: value }))
    },
    [setState]
  )

  const isAddButtonDisabled = !validationResult.success

  return (
    <>
      <GenericDialog
        isOpen={isOpen}
        close={onClose}
        size="2xl"
        title={t('organizationUsers:inviteUserDialog.title')}
        subtitle={t('organizationUsers:inviteUserDialog.description')}
      >
        <div className="space-y-4">
          <InputGroup
            onChange={evt => onInputChange('firstName', evt.target.value)}
            name="firstName"
            label={t('organizationUsers:inviteUserDialog.fields.firstName.label')}
            required
          />
          <InputGroup
            onChange={evt => onInputChange('lastName', evt.target.value)}
            name="lastName"
            label={t('organizationUsers:inviteUserDialog.fields.lastName.label')}
          />
          <EmailInputGroup
            onChange={evt => onInputChange('email', evt.target.value)}
            name="email"
            type="email"
            label={t('organizationUsers:inviteUserDialog.fields.email.label')}
          />
          <div>
            <p className="text-sm mb-2">{t('organizationUsers:permissionsDialog.description')}</p>
            <GenericCombobox
              placeholder={t('organizationUsers:permissionsDialog.fields.selectRole')}
              items={roleOptions || []}
              value={
                state.roleSlug
                  ? { value: state.roleSlug, label: t(`organizationUsers:roles.${state.roleSlug}`) }
                  : null
              }
              onChange={evt => onInputChange('roleSlug', evt?.value || null)}
            />
            {error?.message === 'CONFLICT' && (
              <Alert className="mt-2" type="error">
                {t('organizationUsers:inviteUserDialog.alerts.conflict')}
              </Alert>
            )}
          </div>
          <footer className="flex justify-end gap-2">
            <Button type="button" variant="neutral" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isAddButtonDisabled || isInviting}
              isLoading={isInviting}
              onClick={onAdd}
            >
              {t('common:actions.add')}
            </Button>
          </footer>
        </div>
      </GenericDialog>
    </>
  )
}
